import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProviderList,
  fetchProvidersByCategory,
} from "../services/gameService";
import { buildProviderImageUrl } from "../../utils/providerImage";

const CATEGORY_GROUP_MAP = {
  SLOTS: "slots",
  CASINO: "liveCasino",
  SPORTS: "sportbook",
  CARD: "cardGames",
  FISHING: "fishingGames",
  KENO: "keno",
  LOTTO: "lotto",
};

const createEmptyGroups = () => ({
  slots: [],
  liveCasino: [],
  sportbook: [],
  cardGames: [],
  fishingGames: [],
  keno: [],
  lotto: [],
});

const SLOT_PROVIDER_PRIORITY = [
  "PG Soft",
  "Joker123",
  "Jili",
  "Pragmatic Play (Slot)",
  "Askmebet",
  "Askmeslot",
];

const SLOT_PROVIDER_PRIORITY_INDEX = SLOT_PROVIDER_PRIORITY.reduce(
  (accumulator, providerName, index) => {
    accumulator[providerName.trim().toLowerCase()] = index;
    return accumulator;
  },
  {},
);

const getProviderPriority = (providerName) => {
  const normalizedName = String(providerName || "").trim().toLowerCase();

  return Object.prototype.hasOwnProperty.call(
    SLOT_PROVIDER_PRIORITY_INDEX,
    normalizedName,
  )
    ? SLOT_PROVIDER_PRIORITY_INDEX[normalizedName]
    : Number.POSITIVE_INFINITY;
};

const sortProvidersForCategory = (providers, categoryCode) => {
  if (!Array.isArray(providers)) {
    return [];
  }

  if (String(categoryCode || "").toUpperCase() !== "SLOTS") {
    return providers;
  }

  return [...providers].sort((leftProvider, rightProvider) => {
    const leftPriority = getProviderPriority(leftProvider?.name);
    const rightPriority = getProviderPriority(rightProvider?.name);

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    return 0;
  });
};

const normalizeProvider = (provider, categoryCode) => {
  const image =
    buildProviderImageUrl(provider, categoryCode) || provider?.image || "";

  return {
    id: provider?.id,
    name: provider?.name,
    image,
    logo: image,
    game: provider?.id,
    type: String(categoryCode || "").toLowerCase(),
    status: provider?.status,
    isOpen: provider?.isOpen,
    productId: provider?.id,
    productName: provider?.name,
    categoryCode,
  };
};

// Async thunk for fetching game list
export const fetchGames = createAsyncThunk(
  "games/fetchGames",
  async (_, { rejectWithValue }) => {
    try {
      const categoryResponse = await fetchProviderList();
      const categories = Array.isArray(categoryResponse?.data?.categories)
        ? categoryResponse.data.categories
        : [];
      const groups = createEmptyGroups();

      const results = await Promise.allSettled(
        categories.map(async (category) => {
          const categoryCode = String(category?.code || "").toUpperCase();
          const response = await fetchProvidersByCategory(categoryCode);

          return {
            categoryCode,
            providers: Array.isArray(response?.data?.providers)
              ? response.data.providers
              : [],
          };
        }),
      );

      results.forEach((result) => {
        if (result.status !== "fulfilled") {
          return;
        }

        const groupKey = CATEGORY_GROUP_MAP[result.value.categoryCode];
        if (!groupKey) {
          return;
        }

        groups[groupKey] = result.value.providers.map((provider) =>
          normalizeProvider(provider, result.value.categoryCode),
        );

        groups[groupKey] = sortProvidersForCategory(
          groups[groupKey],
          result.value.categoryCode,
        );
      });

      return groups;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const gameSlice = createSlice({
  name: "games",
  initialState: {
    gameList: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearGames: (state) => {
      state.gameList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGames.fulfilled, (state, action) => {
        state.loading = false;
        state.gameList = action.payload; // now grouped object with compatibility fields
      })
      .addCase(fetchGames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearGames } = gameSlice.actions;
export default gameSlice.reducer;
